import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import ProjectCard from "../components/ProjectCard";
import { Search, Filter as FilterIcon } from "lucide-react";
import { categorySlug } from "../lib/seo";

function buildSearchString(sourceParams) {
  const next = new URLSearchParams(sourceParams);
  next.delete("category");
  next.delete("category_slug");
  const qs = next.toString();
  return qs ? `?${qs}` : "";
}

export default function Projects({ onOpenAuth }) {
  const { segment } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [meta, setMeta] = useState({ categories: [], counties: [], category_slugs: {} });
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [resolvedCategory, setResolvedCategory] = useState(null);

  const q = params.get("q") ?? "";
  const county = params.get("county") ?? "Toate";
  const sort = params.get("sort") ?? "top";
  const paramsString = params.toString();

  const categorySlugMap = useMemo(() => meta.category_slugs || {}, [meta.category_slugs]);
  const categoryNameBySlug = useMemo(() => {
    return Object.fromEntries(Object.entries(categorySlugMap).map(([name, slug]) => [slug, name]));
  }, [categorySlugMap]);

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data));
  }, []);

  useEffect(() => {
    if (!meta.categories.length) return;

    if (segment) {
      const categoryName = categoryNameBySlug[segment];
      if (categoryName) {
        setResolvedCategory(categoryName);
        setRedirecting(false);
        return;
      }

      if (segment.startsWith("proj_")) {
        setRedirecting(true);
        api.get(`/projects/${segment}`)
          .then(({ data }) => {
            navigate(data.canonical_path, { replace: true });
          })
          .catch(() => navigate("/proiecte", { replace: true }));
        return;
      }

      setRedirecting(true);
      navigate("/proiecte", { replace: true });
      return;
    }

    const categoryQuery = params.get("category");
    const categoryQuerySlug = categoryQuery ? categorySlug(categoryQuery) : null;
    if (categoryQuery && (categorySlugMap[categoryQuery] || categoryNameBySlug[categoryQuerySlug])) {
      setRedirecting(true);
      navigate(`/proiecte/${categorySlug(categoryQuery)}${buildSearchString(params)}`, { replace: true });
      return;
    }

    setResolvedCategory(null);
    setRedirecting(false);
  }, [categoryNameBySlug, categorySlugMap, meta.categories.length, navigate, params, paramsString, segment]);

  useEffect(() => {
    if (redirecting) return;
    setLoading(true);

    const query = {};
    if (resolvedCategory) query.category_slug = categorySlug(resolvedCategory);
    if (county && county !== "Toate") query.county = county;
    if (sort) query.sort = sort;
    if (q) query.q = q;

    api.get("/projects", { params: query })
      .then(({ data }) => setProjects(data))
      .finally(() => setLoading(false));
  }, [county, q, redirecting, resolvedCategory, sort]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (key === "category") {
      next.delete("category");
      const nextSearch = buildSearchString(next);
      if (!value || value === "Toate") {
        navigate(`/proiecte${nextSearch}`, { replace: true });
      } else {
        navigate(`/proiecte/${categorySlug(value)}${nextSearch}`, { replace: true });
      }
      return;
    }

    if (!value || value === "Toate") next.delete(key);
    else next.set(key, value);

    const nextSearch = next.toString() ? `?${next.toString()}` : "";
    const path = resolvedCategory ? `/proiecte/${categorySlug(resolvedCategory)}` : "/proiecte";
    navigate(`${path}${nextSearch}`, { replace: true });
  };

  const onVoteChange = (updated) => {
    setProjects((arr) => arr.map((p) => (p.project_id === updated.project_id ? updated : p)));
  };

  const summary = useMemo(() => {
    return `${projects.length} ${projects.length === 1 ? "proiect" : "proiecte"}`;
  }, [projects.length]);

  useEffect(() => {
    document.title = resolvedCategory
      ? `${resolvedCategory} | fără hârtie.ro`
      : "Proiecte | fără hârtie.ro";
  }, [resolvedCategory]);

  if (redirecting) {
    return <div className="container-civic py-16 text-foreground/60">Se încarcă…</div>;
  }

  return (
    <main className="container-civic py-12 md:py-16">
      <header className="grid md:grid-cols-12 gap-8 items-end mb-10">
        <div className="md:col-span-7">
          <div className="label-overline">Proiecte deschise</div>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-[-0.02em] mt-3">
            Votează ce contează<br />pentru orașul tău.
          </h1>
        </div>
        <div className="md:col-span-5 text-sm text-foreground/70">
          Filtrează după județ și categorie. Susții cu <span className="font-semibold text-foreground">Sus</span>, respingi cu <span className="font-semibold text-foreground">Jos</span>. Un vot per proiect.
        </div>
      </header>

      <div className="card-flat p-4 md:p-5 mb-8 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-foreground/70">
          <FilterIcon size={14} /> Filtre
        </div>

        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
          <input
            placeholder="Caută proiecte, autorități, orașe…"
            defaultValue={q}
            onKeyDown={(e) => {
              if (e.key === "Enter") setParam("q", e.currentTarget.value);
            }}
            className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-sm bg-white outline-none focus:border-foreground/50"
            data-testid="search-input"
          />
        </div>

        <select
          value={resolvedCategory || "Toate"}
          onChange={(e) => setParam("category", e.target.value)}
          className="text-sm border border-border rounded-sm bg-white px-3 py-2"
          data-testid="filter-category"
        >
          <option value="Toate">Toate categoriile</option>
          {meta.categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <select
          value={county}
          onChange={(e) => setParam("county", e.target.value)}
          className="text-sm border border-border rounded-sm bg-white px-3 py-2"
          data-testid="filter-county"
        >
          <option value="Toate">Toate județele</option>
          {meta.counties.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <div className="flex items-center gap-1 ml-auto" data-testid="sort-toggle">
          {[
            { key: "top", label: "Top voturi" },
            { key: "new", label: "Recente" },
          ].map((s) => (
            <button
              key={s.key}
              onClick={() => setParam("sort", s.key)}
              className={`text-sm px-3 py-2 rounded-sm border transition-colors ${
                sort === s.key ? "bg-foreground text-background border-foreground" : "border-border bg-white"
              }`}
              data-testid={`sort-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs uppercase tracking-[0.18em] text-foreground/60 font-semibold mb-4">{summary}</p>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card-flat p-6 animate-pulse h-48 bg-secondary/40" />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="card-flat p-12 text-center text-foreground/60">
          Niciun proiect nu corespunde filtrelor selectate.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-6">
          {projects.map((p) => (
            <ProjectCard
              key={p.project_id}
              project={p}
              onVoteChange={onVoteChange}
              requestLogin={() => onOpenAuth?.("login")}
            />
          ))}
        </div>
      )}
    </main>
  );
}
